// Country codes with flags and dial codes.
//
// The list is COMPREHENSIVE on purpose: `findCountryByPhone` can only route a
// pasted/auto-filled country code into the selector if that code exists here.
// A curated 24-country list silently failed for every other code (e.g. a
// pasted +383… landed whole in the national field because +383 wasn't known).
//
// Ordering: a curated priority block (Israel + MENA + major markets) first so
// the dropdown opens on the locally-relevant countries, then every remaining
// country alphabetically. Order does NOT affect detection — findCountryByPhone
// sorts by dial-code length so the most specific code always wins.

// Flag emoji from an ISO-3166 alpha-2 code (two regional-indicator symbols).
const flagFromIso = (iso) =>
  String(iso)
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));

// [ISO2, dialDigits, name] — dial without the leading '+'.
const PRIORITY = [
  ['IL', '972', 'Israel'],
  ['US', '1', 'United States'],
  ['GB', '44', 'United Kingdom'],
  ['IE', '353', 'Ireland'],
  ['PS', '970', 'Palestine'],
  ['SA', '966', 'Saudi Arabia'],
  ['AE', '971', 'United Arab Emirates'],
  ['JO', '962', 'Jordan'],
  ['EG', '20', 'Egypt'],
  ['LB', '961', 'Lebanon'],
  ['IQ', '964', 'Iraq'],
  ['SY', '963', 'Syria'],
  ['YE', '967', 'Yemen'],
  ['KW', '965', 'Kuwait'],
  ['QA', '974', 'Qatar'],
  ['BH', '973', 'Bahrain'],
  ['OM', '968', 'Oman'],
  ['IR', '98', 'Iran'],
  ['FR', '33', 'France'],
  ['DE', '49', 'Germany'],
  ['IT', '39', 'Italy'],
  ['ES', '34', 'Spain'],
  ['CA', '1', 'Canada'],
  ['AU', '61', 'Australia'],
  ['RU', '7', 'Russia'],
  ['CN', '86', 'China'],
  ['IN', '91', 'India'],
  ['JP', '81', 'Japan'],
  ['KR', '82', 'South Korea'],
  ['BR', '55', 'Brazil'],
  ['MX', '52', 'Mexico'],
  ['TR', '90', 'Turkey'],
  ['PK', '92', 'Pakistan'],
];

// Every remaining country, alphabetical by name.
const REST = [
  ['AF', '93', 'Afghanistan'],
  ['AL', '355', 'Albania'],
  ['DZ', '213', 'Algeria'],
  ['AD', '376', 'Andorra'],
  ['AO', '244', 'Angola'],
  ['AG', '1268', 'Antigua and Barbuda'],
  ['AR', '54', 'Argentina'],
  ['AM', '374', 'Armenia'],
  ['AT', '43', 'Austria'],
  ['AZ', '994', 'Azerbaijan'],
  ['BS', '1242', 'Bahamas'],
  ['BD', '880', 'Bangladesh'],
  ['BB', '1246', 'Barbados'],
  ['BY', '375', 'Belarus'],
  ['BE', '32', 'Belgium'],
  ['BZ', '501', 'Belize'],
  ['BJ', '229', 'Benin'],
  ['BT', '975', 'Bhutan'],
  ['BO', '591', 'Bolivia'],
  ['BA', '387', 'Bosnia and Herzegovina'],
  ['BW', '267', 'Botswana'],
  ['BN', '673', 'Brunei'],
  ['BG', '359', 'Bulgaria'],
  ['BF', '226', 'Burkina Faso'],
  ['BI', '257', 'Burundi'],
  ['KH', '855', 'Cambodia'],
  ['CM', '237', 'Cameroon'],
  ['CV', '238', 'Cape Verde'],
  ['CF', '236', 'Central African Republic'],
  ['TD', '235', 'Chad'],
  ['CL', '56', 'Chile'],
  ['CO', '57', 'Colombia'],
  ['KM', '269', 'Comoros'],
  ['CG', '242', 'Congo (Republic)'],
  ['CD', '243', 'Congo (DRC)'],
  ['CR', '506', 'Costa Rica'],
  ['HR', '385', 'Croatia'],
  ['CU', '53', 'Cuba'],
  ['CY', '357', 'Cyprus'],
  ['CZ', '420', 'Czech Republic'],
  ['DK', '45', 'Denmark'],
  ['DJ', '253', 'Djibouti'],
  ['DM', '1767', 'Dominica'],
  ['DO', '1809', 'Dominican Republic'],
  ['EC', '593', 'Ecuador'],
  ['SV', '503', 'El Salvador'],
  ['GQ', '240', 'Equatorial Guinea'],
  ['ER', '291', 'Eritrea'],
  ['EE', '372', 'Estonia'],
  ['SZ', '268', 'Eswatini'],
  ['ET', '251', 'Ethiopia'],
  ['FJ', '679', 'Fiji'],
  ['FI', '358', 'Finland'],
  ['GA', '241', 'Gabon'],
  ['GM', '220', 'Gambia'],
  ['GE', '995', 'Georgia'],
  ['GH', '233', 'Ghana'],
  ['GR', '30', 'Greece'],
  ['GD', '1473', 'Grenada'],
  ['GT', '502', 'Guatemala'],
  ['GN', '224', 'Guinea'],
  ['GW', '245', 'Guinea-Bissau'],
  ['GY', '592', 'Guyana'],
  ['HT', '509', 'Haiti'],
  ['HN', '504', 'Honduras'],
  ['HK', '852', 'Hong Kong'],
  ['HU', '36', 'Hungary'],
  ['IS', '354', 'Iceland'],
  ['ID', '62', 'Indonesia'],
  ['JM', '1876', 'Jamaica'],
  ['KZ', '7', 'Kazakhstan'],
  ['KE', '254', 'Kenya'],
  ['KI', '686', 'Kiribati'],
  ['XK', '383', 'Kosovo'],
  ['KG', '996', 'Kyrgyzstan'],
  ['LA', '856', 'Laos'],
  ['LV', '371', 'Latvia'],
  ['LS', '266', 'Lesotho'],
  ['LR', '231', 'Liberia'],
  ['LY', '218', 'Libya'],
  ['LI', '423', 'Liechtenstein'],
  ['LT', '370', 'Lithuania'],
  ['LU', '352', 'Luxembourg'],
  ['MO', '853', 'Macau'],
  ['MG', '261', 'Madagascar'],
  ['MW', '265', 'Malawi'],
  ['MY', '60', 'Malaysia'],
  ['MV', '960', 'Maldives'],
  ['ML', '223', 'Mali'],
  ['MT', '356', 'Malta'],
  ['MH', '692', 'Marshall Islands'],
  ['MR', '222', 'Mauritania'],
  ['MU', '230', 'Mauritius'],
  ['FM', '691', 'Micronesia'],
  ['MD', '373', 'Moldova'],
  ['MC', '377', 'Monaco'],
  ['MN', '976', 'Mongolia'],
  ['ME', '382', 'Montenegro'],
  ['MA', '212', 'Morocco'],
  ['MZ', '258', 'Mozambique'],
  ['MM', '95', 'Myanmar'],
  ['NA', '264', 'Namibia'],
  ['NR', '674', 'Nauru'],
  ['NP', '977', 'Nepal'],
  ['NL', '31', 'Netherlands'],
  ['NZ', '64', 'New Zealand'],
  ['NI', '505', 'Nicaragua'],
  ['NE', '227', 'Niger'],
  ['NG', '234', 'Nigeria'],
  ['KP', '850', 'North Korea'],
  ['MK', '389', 'North Macedonia'],
  ['NO', '47', 'Norway'],
  ['PW', '680', 'Palau'],
  ['PA', '507', 'Panama'],
  ['PG', '675', 'Papua New Guinea'],
  ['PY', '595', 'Paraguay'],
  ['PE', '51', 'Peru'],
  ['PH', '63', 'Philippines'],
  ['PL', '48', 'Poland'],
  ['PT', '351', 'Portugal'],
  ['RO', '40', 'Romania'],
  ['RW', '250', 'Rwanda'],
  ['KN', '1869', 'Saint Kitts and Nevis'],
  ['LC', '1758', 'Saint Lucia'],
  ['VC', '1784', 'Saint Vincent and the Grenadines'],
  ['WS', '685', 'Samoa'],
  ['SM', '378', 'San Marino'],
  ['ST', '239', 'Sao Tome and Principe'],
  ['SN', '221', 'Senegal'],
  ['RS', '381', 'Serbia'],
  ['SC', '248', 'Seychelles'],
  ['SL', '232', 'Sierra Leone'],
  ['SG', '65', 'Singapore'],
  ['SK', '421', 'Slovakia'],
  ['SI', '386', 'Slovenia'],
  ['SB', '677', 'Solomon Islands'],
  ['SO', '252', 'Somalia'],
  ['ZA', '27', 'South Africa'],
  ['SS', '211', 'South Sudan'],
  ['LK', '94', 'Sri Lanka'],
  ['SD', '249', 'Sudan'],
  ['SR', '597', 'Suriname'],
  ['SE', '46', 'Sweden'],
  ['CH', '41', 'Switzerland'],
  ['TW', '886', 'Taiwan'],
  ['TJ', '992', 'Tajikistan'],
  ['TZ', '255', 'Tanzania'],
  ['TH', '66', 'Thailand'],
  ['TL', '670', 'Timor-Leste'],
  ['TG', '228', 'Togo'],
  ['TO', '676', 'Tonga'],
  ['TT', '1868', 'Trinidad and Tobago'],
  ['TN', '216', 'Tunisia'],
  ['TM', '993', 'Turkmenistan'],
  ['TV', '688', 'Tuvalu'],
  ['UG', '256', 'Uganda'],
  ['UA', '380', 'Ukraine'],
  ['UY', '598', 'Uruguay'],
  ['UZ', '998', 'Uzbekistan'],
  ['VU', '678', 'Vanuatu'],
  ['VA', '379', 'Vatican City'],
  ['VE', '58', 'Venezuela'],
  ['VN', '84', 'Vietnam'],
  ['ZM', '260', 'Zambia'],
  ['ZW', '263', 'Zimbabwe'],
];

export const countryCodes = [...PRIORITY, ...REST].map(([code, dial, name]) => ({
  code,
  dialCode: `+${dial}`,
  name,
  flag: flagFromIso(code),
}));

export const getCountryByCode = (code) =>
  countryCodes.find((c) => c.code === code);

export const getCountryByDialCode = (dialCode) =>
  countryCodes.find((c) => c.dialCode === dialCode);

/** Resolve country from code (IL) or dial digits (972) */
export const getCountryByCodeOrDial = (codeOrDial) => {
  if (!codeOrDial) return null;
  const s = String(codeOrDial).trim();
  if (s.length === 2) return getCountryByCode(s.toUpperCase());
  const dial = s.startsWith('+') ? s : '+' + s;
  return getCountryByDialCode(dial) || countryCodes.find((c) => c.dialCode === dial);
};

// Match longest dial code first (e.g. +1268 before +1) so the most specific
// country wins. Duplicate dial codes (e.g. +1 US/CA, +7 RU/KZ) resolve to
// whichever appears first in `countryCodes` — the priority block guarantees the
// canonical country (US, Russia) wins.
export const findCountryByPhone = (phoneOrDigits) => {
  const digits = String(phoneOrDigits || '').replace(/\D/g, '');
  if (!digits) return null;
  const sorted = [...countryCodes].sort(
    (a, b) =>
      (b.dialCode?.replace(/\D/g, '')?.length || 0) -
      (a.dialCode?.replace(/\D/g, '')?.length || 0)
  );
  for (const c of sorted) {
    const codeDigits = c.dialCode.replace(/\D/g, '');
    if (codeDigits && digits.startsWith(codeDigits)) return c;
  }
  return null;
};

export const defaultCountry = countryCodes[0];

// Reorder a country list for DISPLAY only — move pinned ISO codes to the end
// (default: Israel). Detection (findCountryByPhone) and the default selection
// read the unmodified source array, so this changes dropdown order only.
/**
 * Countries kept OUT of the picker, while staying in the data.
 *
 * From late September 2026 a steady trickle of sign-ups arrived on +91 and
 * +92 — grocery stalls, tea shops, ten of them by 2 November, not one of which
 * ever booked an appointment. The owner asked for the two dial codes to come
 * off the picker.
 *
 * They are hidden HERE, in the display path, and not deleted from the list
 * above — deleting them would undo this file's own opening fix.
 * `findCountryByPhone` resolves a pasted or stored number only when its dial
 * code exists in the source list, so a deletion would send every existing +91
 * number whole into the national field: the businesses already on the system
 * would break, and so would anyone's Indian customer.
 *
 * What this does and does not do: it removes two rows from a dropdown. It is
 * friction, not a border. Someone determined can still pick another country
 * and type whatever digits they like, and nothing here touches the sign-up API.
 * If these accounts keep arriving, the tap is further upstream than this list.
 */
export const HIDDEN_FROM_PICKER = new Set(['IN', 'PK']);

export const reorderForDisplay = (list, pinnedToEnd = ['IL']) => {
  const pinned = new Set(pinnedToEnd);
  const rest = (list || [])
    .filter((c) => !HIDDEN_FROM_PICKER.has(c.code)).filter((c) => !pinned.has(c.code));
  const tail = pinnedToEnd
    .filter((code) => !HIDDEN_FROM_PICKER.has(code))
    .map((code) => (list || []).find((c) => c.code === code))
    .filter(Boolean);
  return [...rest, ...tail];
};
